/**
 * Google Places Autocomplete input for address fields.
 * Uses @vis.gl/react-google-maps useMapsLibrary("places") hook.
 * When user selects an address:
 *   - onAddressSelect({ address, lat, lng }) is called
 */
import React, { useEffect, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

export default function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  className,
  placeholder = "Adresse eingeben…",
}) {
  const placesLib = useMapsLibrary("places");
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;

    autocompleteRef.current = new placesLib.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "ch" },
      fields: ["formatted_address", "geometry"],
    });

    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();
      if (!place || !place.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || "";
      onAddressSelect({ address, lat, lng });
    });

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [placesLib, onAddressSelect]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={onChange}
      className={className}
      placeholder={placeholder}
    />
  );
}
